import { useState } from 'react';
import { AxiosError } from 'axios';
import { showToast } from '@/services/toast-service';
import { createContract } from '@/lib/coloc-api/admin';
import { ContractCreateData } from '@/features/admin/contracts/types/contract-create';

interface UseCreateContractReturnType {
  createContract: (contractData: ContractCreateData) => Promise<boolean>;
  isCreating: boolean;
  error: Error | null;
  showSuccessModal: boolean;
  setShowSuccessModal: (show: boolean) => void;
}

export const useCreateContract = (): UseCreateContractReturnType => {
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  const handleCreateContract = async (contractData: ContractCreateData): Promise<boolean> => {
    try {
      setIsCreating(true);
      setError(null);

      await createContract(contractData);
      
      const isExistingColoc = !!contractData.coloc.id;
      const successMessage = isExistingColoc
        ? 'Contract created successfully (coloc already existed).'
        : 'Contract created successfully and invitation sent to coloc!';
      
      showToast.success({ text: successMessage });
      setShowSuccessModal(true);
      return true;
    } catch (error) {

      const err = error instanceof Error ? error : new Error('Failed to create contract');
      setError(err);
      
      let errorMessage = 'Failed to create contract. Please try again.';
      if (error instanceof AxiosError && error.response?.data?.errors) {
        errorMessage = `Error: ${error.response.data.errors}`;
      }
      
      console.error('Failed to create contract:', error);
      showToast.error({ text: errorMessage });
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createContract: handleCreateContract,
    isCreating,
    error,
    showSuccessModal,
    setShowSuccessModal
  };
}; 